import { useEffect } from "react";

import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";

import BillingCycle from "./BillingCycle";
import useBillingStyles from "./BillingStyles";
import {
  loadMore,
  loadPastCharges,
  loadPastStatementInformation,
} from "../../store/modules/pastCharges/pastChargesActions";
import { RootState } from "../../store/store";
import theme from "../../theme";
import { StatementPaymentStatus } from "../../types/types";
import { TransactionClassAttendance } from "../../utils/constants";
import { useIsMaguireEnabled, useMaguireWeeklyBillingCycleCutOffId } from "../../utils/maguireHelper";

function PastCharges() {
  const isMaguireEnabled = useIsMaguireEnabled();
  const weeklyBillingCycleCutOffId = useMaguireWeeklyBillingCycleCutOffId();
  const dispatch = useDispatch();
  const customer_account_id = useSelector((state: RootState) => state.auth.user?.profile.customer_account_id);
  const pastCharges = useSelector((state: RootState) => state.pastCharges).pastChargesState.pastCharges;
  const isMorePastChargesLoading = useSelector((state: RootState) => state.pastCharges).pastChargesState.isMoreLoading;
  const isPastChargesLoaded = useSelector((state: RootState) => state.pastCharges.pastChargesState.isLoaded);
  const pageIndex = useSelector((state: RootState) => state.pastCharges.pastChargesState.page);
  const isLastPage = useSelector((state: RootState) => state.pastCharges.pastChargesState.isLastPage);

  const statementInformation = useSelector((state: RootState) => state.pastCharges).statementInformationState
    .statementInformation;
  const isstatementInformationLoaded = useSelector((state: RootState) => state.pastCharges).statementInformationState
    .isLoaded;

  const { classes: billingClasses } = useBillingStyles();

  useEffect(() => {
    if (customer_account_id && !isPastChargesLoaded && pageIndex === 1) {
      loadPastCharges(dispatch, customer_account_id, 5, 1);
    }
  }, [customer_account_id, isPastChargesLoaded, pageIndex]);

  const handleShowMore = () => {
    loadMore(dispatch, customer_account_id ?? 0, 5, pageIndex + 1);
  };

  const LoadMoreButton = () => {
    if (isMorePastChargesLoading === false && isLastPage === false) {
      return (
        <Button onClick={handleShowMore}>
          <strong className="color-accent-2">Load more</strong>
        </Button>
      );
    }
    return <div></div>;
  };
  useEffect(() => {
    if (customer_account_id && !isstatementInformationLoaded && pageIndex === 1 && isMaguireEnabled) {
      loadPastStatementInformation(dispatch, customer_account_id, 5, 1);
    }
  }, [customer_account_id, isstatementInformationLoaded, pageIndex, isMaguireEnabled]);

  if (!isMaguireEnabled) {
    if (pastCharges && pastCharges.length > 0) {
      return (
        <>
          <h2 className={billingClasses.billingSectionHeader}>
            {isMaguireEnabled ? "Current Statement" : "Past Charges"}
          </h2>
          {pastCharges.map((item, index) => {
            const fromDate = format(item.billingPeriodStart.toDate(), "dd MMM");
            const toDate = format(item.billingPeriodEnd.toDate(), "dd MMM yyyy");
            const formattedDateRange = `${fromDate} - ${toDate}`;
            var totalBookingsAmount = item.transactions
              .filter((t) => t.transactionClassId === TransactionClassAttendance && t.reversalDate === undefined)
              .reduce((a, b) => a + (b.fees ?? 0), 0);
            var totalCCSAndACCSAmount = item.transactions.reduce((a, b) => a + (b.ccs + b.accs), 0);
            var totalOtherFees = item.transactions
              .filter((t) => t.transactionClassId !== TransactionClassAttendance && t.reversalDate === undefined)
              .reduce((a, b) => a + (b.totalDebits ?? 0), 0);
            var credits = item.transactions.reduce((a, b) => a + (b.parentFeeWaiver ?? 0), 0);
            var totalOutOfPocketPeriod =
              totalBookingsAmount - Math.abs(totalCCSAndACCSAmount) + totalOtherFees + credits;
            return (
              <BillingCycle
                key={index}
                customerAccountId={customer_account_id as number}
                billingCycleId={item.billingCycleId}
                billingPeriodEnd={isMaguireEnabled ? formattedDateRange : item.billingPeriodEnd}
                totalAmount={totalOutOfPocketPeriod ?? 0}
                billingCycleStatus={item.isStatementCleared ? "" : item.isOverDue ? "OverDue" : item.isDue ? "Due" : ""}
                statusColor={
                  item.isStatementCleared
                    ? theme.palette.success.light
                    : item.isOverDue
                    ? theme.palette.error.light
                    : item.isDue
                    ? theme.palette.warning.light
                    : theme.palette.info.light
                }
                totalBookingsAmount={totalBookingsAmount}
                totalCCSAndACCSAmount={totalCCSAndACCSAmount}
                totalOtherFees={totalOtherFees}
                totalNumberOfBookings={item.transactions.length}
                transactions={item.transactions}
              ></BillingCycle>
            );
          })}
          <Grid container justifyContent="center">
            <LoadMoreButton />
          </Grid>
        </>
      );
    }
  } else {
    const filteredStatementInformation = statementInformation.filter(
      (item) => item.billingCycleId >= weeklyBillingCycleCutOffId
    );
    const filteredPastCharges = pastCharges.filter((item) => item.billingCycleId < weeklyBillingCycleCutOffId);

    if (filteredStatementInformation && filteredStatementInformation.length > 0) {
      return (
        <>
          <h2 className={billingClasses.billingSectionHeader}>Current Statement</h2>
          {filteredStatementInformation.map((item, index) => {
            const fromDate = format(item.fromDate.toDate(), "dd MMM");
            const toDate = format(item.toDate.toDate(), "dd MMM yyyy");
            const formattedDateRange = `${fromDate} - ${toDate}`;
            const filteredTransactions = item.statementInformationDetail.filter((tx) => {
              if (tx.transactionClassId === TransactionClassAttendance) {
                return (
                  tx.transactionDate &&
                  tx.transactionDate.isAfter(item.fromDate.subtract(1, "day")) &&
                  tx.transactionDate.isBefore(item.toDate.add(1, "day"))
                );
              }
              return true;
            });
            return (
              <BillingCycle
                key={index}
                customerAccountId={customer_account_id as number}
                billingCycleId={item.billingCycleId}
                billingPeriodEnd={formattedDateRange}
                totalAmount={item.totalAmount ?? 0}
                billingCycleStatus={
                  item.paymentStatusId === StatementPaymentStatus.OverDue
                    ? "OverDue"
                    : item.paymentStatusId === StatementPaymentStatus.Due
                    ? "Due"
                    : ""
                }
                statusColor={
                  item.paymentStatusId === StatementPaymentStatus.Paid
                    ? theme.palette.success.light
                    : item.paymentStatusId === StatementPaymentStatus.OverDue
                    ? theme.palette.error.light
                    : item.paymentStatusId === StatementPaymentStatus.Due
                    ? theme.palette.warning.light
                    : theme.palette.info.light
                }
                totalBookingsAmount={!!item.bookingCharges ? item.bookingCharges : 0}
                totalCCSAndACCSAmount={(item.ccs || 0) + (item.accs || 0)}
                totalOtherFees={!!item.otherCharges ? item.otherCharges : 0}
                totalNumberOfBookings={filteredTransactions.length}
                debtCategoryId={item.debtCategoryId}
                statementId={item.statementId}
                transactions={filteredTransactions}
                isWeeklyStatement={true}
              ></BillingCycle>
            );
          })}
        </>
      );
    } else if (filteredPastCharges && filteredPastCharges.length > 0) {
      return (
        <>
          <h2 className={billingClasses.billingSectionHeader}>
            {isMaguireEnabled ? "Current Statement" : "Past Charges"}
          </h2>
          {filteredPastCharges.map((item, index) => {
            const fromDate = format(item.billingPeriodStart.toDate(), "dd MMM");
            const toDate = format(item.billingPeriodEnd.toDate(), "dd MMM yyyy");
            const formattedDateRange = `${fromDate} - ${toDate}`;
            var totalBookingsAmount = item.transactions
              .filter((t) => t.transactionClassId === TransactionClassAttendance && t.reversalDate === undefined)
              .reduce((a, b) => a + (b.fees ?? 0), 0);
            var totalCCSAndACCSAmount = item.transactions.reduce((a, b) => a + (b.ccs + b.accs), 0);
            var totalOtherFees = item.transactions
              .filter((t) => t.transactionClassId !== TransactionClassAttendance && t.reversalDate === undefined)
              .reduce((a, b) => a + (b.totalDebits ?? 0), 0);
            var credits = item.transactions.reduce((a, b) => a + (b.parentFeeWaiver ?? 0), 0);
            var totalOutOfPocketPeriod =
              totalBookingsAmount - Math.abs(totalCCSAndACCSAmount) + totalOtherFees + credits;
            return (
              <BillingCycle
                key={index}
                customerAccountId={customer_account_id as number}
                billingCycleId={item.billingCycleId}
                billingPeriodEnd={isMaguireEnabled ? formattedDateRange : item.billingPeriodEnd}
                totalAmount={totalOutOfPocketPeriod ?? 0}
                billingCycleStatus={item.isStatementCleared ? "" : item.isOverDue ? "OverDue" : item.isDue ? "Due" : ""}
                statusColor={
                  item.isStatementCleared
                    ? theme.palette.success.light
                    : item.isOverDue
                    ? theme.palette.error.light
                    : item.isDue
                    ? theme.palette.warning.light
                    : theme.palette.info.light
                }
                totalBookingsAmount={totalBookingsAmount}
                totalCCSAndACCSAmount={totalCCSAndACCSAmount}
                totalOtherFees={totalOtherFees}
                totalNumberOfBookings={item.transactions.length}
                transactions={item.transactions}
              ></BillingCycle>
            );
          })}
          <Grid container justifyContent="center">
            <LoadMoreButton />
          </Grid>
        </>
      );
    } else {
      return null;
    }
  }
  return null as any;
}

export default PastCharges;
